import { memo } from "react";

/**
 * LoopVideo — the decorative autoplay loop used on the marketing surfaces.
 *
 * Marketing pages show product recordings as silent wallpaper: they start on
 * their own, repeat forever, and carry no controls, so the only thing a reader
 * can do with one is look at it. That whole posture is six attributes
 * (`autoPlay muted loop playsInline preload="metadata"` plus an `aria-label`,
 * because a controlless video is an image to everyone who cannot see it) and it
 * was written out four times across three files. It lives here once.
 *
 * NOT barreled in src/components/ui/atoms/index.ts, for the same reason
 * index.ts keeps JsonLdScript out: plates.tsx and one-system.tsx are server
 * components, the barrel re-exports client-only atoms, and importing the barrel
 * would drag those into the client bundle. Deep-import it:
 *
 *     import { LoopVideo } from "@/components/ui/atoms/LoopVideo";
 *
 * The atom owns no CSS. Each slot sizes its own video — the plates band crops
 * to a fixed aspect, the one-system figure runs at the recording's intrinsic
 * height, the features card crops from the left edge — and in three of the four
 * slots the class is shared with an `<Image>` that stands in when there is no
 * recording. Styling the element from here would split that pairing across two
 * sheets, so `className` is required and the slot supplies it.
 *
 * Deliberately not a consumer: spectrum-hero.tsx's teaser player. It has
 * `controls`, no `loop`, and opens in a lightbox — a thing you watch, not
 * wallpaper.
 */
export const LoopVideo = memo(function LoopVideo({
    src,
    poster,
    label,
    className,
}: {
    src: string;
    /** First frame to hold while the recording loads. Only some slots have one. */
    poster?: string;
    /** What the recording shows, for readers who cannot watch it. */
    label: string;
    /** The slot's own class — this atom brings no styling of its own. */
    className: string;
}) {
    return (
        <video
            className={className}
            src={src}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={label}
        data-component="LoopVideo" data-location="src/components/ui/atoms/LoopVideo.tsx"/>
    );
});
